import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthShell from '../../components/auth/AuthShell';
import { Loader2, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const redirectAfter = (data) => {
    const from = loc.state?.from || '/home';
    nav(data?.onboarding_complete === false ? '/onboarding' : from, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.success) { setError(res.error); return; }
    redirectAfter(res.data);
  };

  const demoInvestorLogin = async () => {
    setLoading(true);
    setError(null);
    const res = await login('investor@wave.app', 'WaveDemo2026!');
    setLoading(false);
    if (!res.success) { setError(res.error); return; }
    redirectAfter(res.data);
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to ride your next Wave." testId="login-page"
      footer={<span className="text-gray-600">Closed beta. Accounts by invitation only.</span>}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Email</label>
          <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                 data-testid="login-email-input"
                 className="w-full mt-1.5 px-4 py-3 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] focus:border-[#00D4FF] text-sm outline-none transition" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Password</label>
            <Link to="/forgot-password" className="text-[10px] text-[#00D4FF] hover:underline" data-testid="forgot-link">Forgot?</Link>
          </div>
          <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
                 data-testid="login-password-input"
                 className="w-full mt-1.5 px-4 py-3 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] focus:border-[#00D4FF] text-sm outline-none transition" />
        </div>
        {error && (
          <div className="flex items-start gap-2 text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="login-error">
            <AlertCircle className="h-3.5 w-3.5 mt-px" /> <span>{error}</span>
          </div>
        )}
        <button type="submit" disabled={loading} data-testid="login-submit-btn"
                className="w-full py-3.5 rounded-lg bg-[#FF4D94] text-white font-bold text-sm transition-all hover:brightness-110 hover:scale-[1.01] disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ boxShadow: '0 0 20px rgba(255,77,148,0.35)' }}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
        </button>
      </form>

      {/* One-tap investor login — gated behind REACT_APP_DEMO_MODE so it's
          completely absent from production builds where DEMO_MODE_ENABLED
          is not set. The backend /auth/demo-login endpoint is also gated.
          Google OAuth intentionally removed: public cannot create accounts
          (closed beta — brand accounts are seeded by the founder). */}
      {process.env.REACT_APP_DEMO_MODE === 'true' && (
        <button onClick={demoInvestorLogin} disabled={loading} data-testid="demo-investor-login"
                className="w-full mt-3 py-2 rounded-lg border border-dashed border-[#FF4D94]/30 bg-transparent text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF4D94]/70 hover:text-[#FF4D94] hover:border-[#FF4D94]/60 transition-all">
          Investor demo: one-tap login
        </button>
      )}
    </AuthShell>
  );
}
